import { get } from 'svelte/store';
//Helpers
import { isPill, real_id } from '../interface_helpers';
import { getAddonStatus, hasAddon } from './addon_helpers';
import { getLoraAddonStatus } from './addon_lora_helpers';
import { isProSensorAddon } from './addon_support_helpers';
//Services
import { postReq } from 'src/services/http';
//Stores
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
//Types
import type { Device, Devices } from '../../types/Device';
import type { Peripheral } from 'src/types/Peripherals';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';

const peripheralDeviceChannels = {
	temperature: 0,
	switch: 0,
	lora: 0,
	humidity: 1,
	voltmeter: 5,
	input: 10,
	digital_out: 20,
	pro_addon_humidity: 30,
	pro_addon_voltmeter: 40,
};

/**
 * Retrieves a peripheral device by parent ID.
 * @param {Device['id']} parentID - The ID of the parent device.
 * @param {Peripheral} peripheral - The peripheral object.
 * @returns {Device} The corresponding peripheral device.
 */
export const getPeripheralDevice = (parentID: Device['id'], peripheral: Peripheral): Device => {
	const deviceID = getPeripheralDeviceIDByParentID(parentID, peripheral);
	return get(devices)[deviceID];
};

/**
 * Retrieves the device channel corresponding to a peripheral.
 * @param {Peripheral} peripheral - The peripheral object.
 * @returns {number} The calculated device channel.
 */
const getDeviceChannelByPeripheral = (peripheral: Peripheral): number => {
	let channel = peripheral.sensor.extid;
	let type = peripheral.sensor.type;
	const device = get(devices)[real_id(peripheral.deviceID)];
	if (isPill(device?.type)) {
		channel = channel - 100;
		type = type === 'switch' ? 'digital_out' : type;
	} else if (isProSensorAddon(device)) {
		if (type === 'switch') {
			type = 'digital_out';
		} else if (type === 'humidity') {
			type = 'pro_addon_humidity';
		} else if (type === 'voltmeter') {
			type = 'pro_addon_voltmeter';
		}
	}
	return channel + peripheralDeviceChannels[type];
};

/**
 * Retrieves all peripheral devices associated with a parent device ID.
 * @param {Device['id']} parentID - The ID of the parent device.
 * @param {Devices} devices - The collection of all devices.
 * @returns {Device[]} An array of peripheral devices.
 */
const getPeripheralDevicesByParentID = (parentID: Device['id'], devices: Devices): Device[] => {
	return Object.values(devices).filter((dev) => new RegExp(`^${parentID}_1\\d{2}$`).test(dev.id));
};

/**
 * Retrieves the switch-type peripheral device associated with a parent device ID.
 * @param {Device['id']} parentID - The ID of the parent device.
 * @param {number} [componentID=100] - The component ID of the switch peripheral (default is 100).
 * @returns {Device | null} The switch peripheral device if found, otherwise `null`.
 */
export const getSwitchPeripheralDeviceByParentID = (
	parentID: Device['id'],
	componentID = 100,
): Device | null => {
	const peripheralDevices = getPeripheralDevicesByParentID(parentID, get(devices));
	return peripheralDevices.find((dev) => dev?.component === `switch:${componentID}`) || null;
};

/**
 * Checks if a device has a switch-type peripheral.
 *
 * @param {Device['id']} deviceID - The ID of the device.
 * @returns {boolean} `true` if the device has a switch peripheral, otherwise `false`.
 */
export const hasSwitchPeripheral = (deviceID: Device['id']): boolean => {
	return getSwitchPeripheralsChannel(deviceID).length > 0;
};

/**
 * Retrieves an array of switch peripheral channels associated with a device.
 * @param {Device['id']} deviceID - The ID of the device.
 * @returns {number[]} An array of switch peripheral channels.
 * @example
 * const switchChannels = getSwitchPeripheralsChannel(deviceID);
 * console.log(switchChannels); // [100, 101, 102]
 */
export const getSwitchPeripheralsChannel = (deviceID: Device['id']): number[] => {
	const deviceSettings = devices_settings.getByRealId(deviceID);
	return Object.keys(deviceSettings || {})
		.sort()
		.filter((key) => /^switch:\d{3}$/.test(key))
		.map((key) => deviceSettings[key].id);
};

/**
 * Retrieves an array of event log tags for switch-type peripherals of a device.
 * @param {Device['id']} parentID - The ID of the parent device.
 * @param {boolean} [skipExtractedDevices=false] - Skips extracted devices in the event log tags.
 * If the peripheral switch device has been extracted,
 * it gets its own event log section and is removed from the parent device's event log.
 * @returns {string[]} An array of event log tags.
 */
export const getSwitchPeripheralsTags = (parentID: Device['id'], skipExtractedDevices = false) => {
	const switchPeripherals = getSwitchPeripheralsChannel(parentID).filter(
		(channel) => !skipExtractedDevices || !getSwitchPeripheralDeviceByParentID(parentID, channel),
	);
	return switchPeripherals.map((channel) => `${parentID}_${channel}`);
};

const _devicesInProgress = {
	created: {},
	deleted: {},
};

/**
 * Generates a peripheral device ID based on parent ID and peripheral details.
 * @param {Device['id']} parentID - The ID of the parent device.
 * @param {Peripheral} peripheral - The peripheral object.
 * @returns {Device['id']} The generated device ID.
 */
export const getPeripheralDeviceIDByParentID = (
	parentID: Device['id'],
	peripheral: Peripheral,
): Device['id'] => {
	const id = real_id(parentID);
	const channel = getDeviceChannelByPeripheral(peripheral);
	return `${id}_${channel}`;
};

/**
 * Creates a new peripheral device.
 * @param {Device} device - The parent device.
 * @param {Peripheral} peripheral - The peripheral details.
 * @returns {Promise<CloudApiResponse>} API response from the creation request.
 */
export const createPeripheralDevice = async (
	device: Device,
	peripheral: Peripheral,
): Promise<CloudApiResponse> => {
	const id = real_id(device.id);
	const channel = getDeviceChannelByPeripheral(peripheral);
	const peripheralDeviceID = `${id}_${channel}`;
	if (_devicesInProgress.created[peripheralDeviceID]) {
		return;
	}
	_devicesInProgress.created[peripheralDeviceID] = 1;
	const positions = Object.values(get(devices) || [])
		.filter((v: Device) => v.room_id == device.room_id)
		.map((i) => i.position);
	let position = Math.max.apply(null, positions.length ? positions : [0]) + 1;
	const peripheralType = peripheral.peripheralType;
	const peripheralCategory = /^(switch)$/.test(peripheralType)
		? 'relay'
		: /^(lora)$/.test(peripheralType)
			? peripheralType
			: 'sensor';
	const peripheralDevice: Device = {
		id: peripheralDeviceID,
		channel,
		channels_count: 1,
		gen: device.gen,
		room_id: device.room_id,
		image: '',
		category: peripheralCategory,
		mode: peripheralCategory,
		component: peripheral.sensor.component,
		peripheral_type: peripheralType,
		type: 'peripheral-device',
		name: peripheral.settings.name || peripheral.sensor.label,
		cloud_options: { exclude_event_log: false },
		icon: peripheral.icon,
		ip: device.ip,
		jti: device.jti || '',
		createdAt: Date.now(),
		position,
	};
	const res: CloudApiResponse = await postReq(`/interface/device/save`, {
		id: peripheralDeviceID,
		data: JSON.stringify(peripheralDevice),
	});
	if (res?.isok) {
		devices.update(peripheralDevice);
	}
	_devicesInProgress.created[peripheralDeviceID] = 0;
	return res;
};

/**
 * Deletes a peripheral device.
 * @param {Device} device - The device to be deleted.
 * @returns {Promise<CloudApiResponse>} API response from the deletion request.
 */
export const deletePeripheralDevice = async (device: Device): Promise<CloudApiResponse> => {
	const id = device.id;
	if (_devicesInProgress.deleted[id]) {
		return;
	}
	_devicesInProgress.deleted[id] = 1;
	const res: CloudApiResponse = await postReq(`/interface/device/bulk_delete`, {
		ids: JSON.stringify([id]),
	});
	if (res?.isok) {
		devices.remove(id);
		devices_status.remove(id);
		devices_settings.remove(id);
	}
	_devicesInProgress.deleted[id] = 0;
	return res;
};

/**
 * Deletes all peripheral children devices linked to a parent device.
 * @param {Device} device - The parent device.
 */
export const deletePeripheralChildren = async (device: Device) => {
	const id = real_id(device.id);
	const allDevices = get(devices);
	for (const dev in allDevices) {
		if (new RegExp(`${id}_1\\d{2}$`).test(dev)) {
			await deletePeripheralDevice(allDevices[dev]);
		}
	}
};

/**
 * Retrieves the status of a peripheral device, including its addon status.
 *
 * @param {Device} device - The peripheral device for which the status is being retrieved.
 * @param {Peripheral[]} [interface_data=[]] - Optional array of interface data to be used as a base status.
 * @returns {Peripheral[]} An array containing the status of the peripheral device.
 */
export const getPeripheralDeviceStatus = (device: Device, interface_data = []): Peripheral[] => {
	if (device?.mode == 'lora') {
		return getLoraAddonStatus(device, interface_data);
	}
	const peripheral = getAddonStatus(device).find((i) => i.sensor.component == device.component);
	if (peripheral) {
		return [{ ...peripheral, mainLabel: true }];
	}
	return interface_data;
};

export const isPeripheralDevice = (deviceType: string): boolean => {
	return deviceType == 'peripheral-device';
};

/**
 * Retrieves peripheral interface data for a given device.
 * @param {Device} device - The main device.
 * @param {[key: string]: any} data - Interface data of the main device.
 * @returns {[key: string]: any} A copy of the main device's relevant interface data.
 */
export const getPeripheralInterfaceData = (
	device: Device,
	data: {
		[key: string]: any;
	},
): { [key: string]: any } => {
	const interfaceData = {};
	if (hasAddon(device)) {
		const allDevices = get(devices);
		Object.keys(allDevices).forEach((deviceID) => {
			const dev = allDevices[deviceID];
			if (
				new RegExp(`^${device.id}_1\\d{2}$`).test(deviceID) && // Ensured that it's a peripheral device associated with the main device
				data[dev?.component] // Ensured that the component is present in the interface data
			) {
				interfaceData[deviceID] = data;
			}
		});
	}
	return interfaceData;
};

export const getPeripheralDigitalInputDevices = (device: Device, filtered: Device[] = []): Device[] => {
	const peripherals = getAddonStatus(device);
	if (!peripherals.length) return filtered;
	peripherals.forEach((peripheral) => {
		if (peripheral.peripheralType === 'input' && peripheral.sensor.inputType === 'switch') {
			const ext_sen_id = `i${Number(peripheral.sensor.extid) + 1}`;
			const deviceID = real_id(device.id);
			if (
				filtered.find((f) => real_id(f.id) == deviceID && f.ext_sen_id == ext_sen_id) ==
				undefined
			) {
				const peripheralDevice = getPeripheralDevice(real_id(device.id), peripheral);
				if (peripheralDevice) {
					filtered.push({
						...peripheralDevice,
						id: deviceID,
						ext_sen_id,
						peripheralDeviceID: peripheralDevice.id,
					});
				} else if (device.channel === 0) {
					filtered.push({
						...device,
						name: `${peripheral.sensor.name} ( ${device.name} )`,
						ext_sen_id,
					});
				}
			}
		}
	});
	return filtered;
};
